import React, { useMemo, memo } from 'react';
import { Box, Image, PressableIcon, Text } from '@bonanza-social/moonlight';
import { useTranslation } from '@bonanza-social/i18n';

import { BottomSheetHeaderProps } from './BottomSheetModal.types';
import {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { BottomSheetHandleProps, useBottomSheet } from '@gorhom/bottom-sheet';
import { PressableIconProps } from '@bonanza-social/moonlight/lib/common/PressableIcon/PressableIcon.types';

function BottomSheetHeader({
  title,
  titleKey,
  titleNS,
  showCloseIcon,
  light,
  imageName,
  onInfo,
  animatedIndex,
}: BottomSheetHandleProps & BottomSheetHeaderProps) {
  const { close } = useBottomSheet();
  const [t] = useTranslation(titleNS);
  const opacity = useAnimatedStyle(() => ({
    opacity: interpolate(
      animatedIndex.value,
      [-1, 0],
      [0, 1],
      Extrapolation.CLAMP,
    ),
  }));

  const Icons = useMemo(() => {
    const colorProps: Partial<PressableIconProps> = light
      ? { color: 'accentSurfacePrimary' }
      : { gradient: 'accentBackground' };

    return (
      <Box flexDirection="row" alignItems="center" gap="space-200">
        {onInfo ? (
          <PressableIcon
            name="info"
            width="space-600"
            height="space-600"
            onPress={onInfo}
            {...colorProps}
          />
        ) : null}
        {showCloseIcon ? (
          <PressableIcon
            name="close"
            width="space-600"
            height="space-600"
            onPress={() => close()}
            testID="bottom-sheet-header-close"
            {...colorProps}
          />
        ) : null}
      </Box>
    );
  }, [light, onInfo, showCloseIcon, close]);

  return (
    <Box
      flexDirection="row"
      justifyContent="flex-end"
      gap="space-600"
      paddingHorizontal="space-600"
      paddingTop="space-600"
      paddingBottom={title ? 'space-400' : 'space-0'}
    >
      {(titleKey || title) && (
        <Text
          flex={1}
          variant="h5"
          color="darkSurfacePrimary"
          numberOfLines={2}
          wordBreak="break-word"
        >
          {t(titleKey || '') || title}
        </Text>
      )}
      {Icons}
      {imageName ? (
        <Box
          position="absolute"
          left="space-0"
          right="space-0"
          top="-space-1000"
          pointerEvents="none"
          animatedStyle={opacity}
        >
          <Image
            alignSelf="center"
            name={imageName}
            aspectRatio={1.55}
            height="space-2000"
          />
        </Box>
      ) : null}
    </Box>
  );
}

export default memo(BottomSheetHeader);
